import { useMyCompany } from './company/useCompany';

/**
 * Product entitlements hook — the single frontend source of truth for whether a
 * module is enabled for the current company.
 *
 * Sourcing: reads `company.products` from `useMyCompany()`, which fetches
 * GET /companies/my-company (SWR, cached). That endpoint returns the full
 * Company document, so `products` is included automatically once present on the
 * schema — no API change was needed.
 *
 * Backward-compatible default = ON: `hasModule(name)` returns true unless the
 * company explicitly set `products[name] === false`. A missing `products` field,
 * an undefined module, or a still-loading company all resolve to enabled, so
 * existing companies and the initial render are never wrongly locked out.
 *
 * @returns {{ products: object|undefined, hasModule: (name: string) => boolean, isLoading: boolean }}
 */
export function useEntitlements() {
  const { company, isLoading } = useMyCompany();
  const products = company?.products;

  // Only an explicit `false` disables a module. Everything else = enabled.
  const hasModule = (name) => products?.[name] !== false;

  return { products, hasModule, isLoading };
}

export default useEntitlements;
